import { createServerOnlyFn } from "@tanstack/react-start";
import {
    kitsConversationParticipants,
    userProfileFacts,
    userProfiles,
} from "@wags/db/schema";
import type { BoundaryUnknown } from "@wags/utils/boundary-unknown.ts";
import { COMPAT_FREE_SCORE_FACT_KEY } from "@wags/viewer/index.ts";
import { and, eq } from "drizzle-orm";
import { getDb } from "@/lib/core/db.server";
import { freeScoreLedgerSchema } from "./compat-score-gate.contract";

type CompatScoreGateDecision =
    | "signed_in"
    | "free_look"
    | "re_serve"
    | "locked";

export function parseFreeScoreLedger(
    value: BoundaryUnknown,
): { listingId: string | null } | null {
    return freeScoreLedgerSchema.parse(value) ?? null;
}

export function decideCompatScoreGate(input: {
    isAnonymous: boolean;

    priorFreeScore: { listingId: string | null } | null;
    requestedListingId: string;
}): { decision: CompatScoreGateDecision; forceDeterministic: boolean } {
    if (!input.isAnonymous) {
        return { decision: "signed_in", forceDeterministic: false };
    }
    if (input.priorFreeScore === null) {
        return { decision: "free_look", forceDeterministic: true };
    }
    if (input.priorFreeScore.listingId === input.requestedListingId) {
        return { decision: "re_serve", forceDeterministic: true };
    }
    return { decision: "locked", forceDeterministic: true };
}

export const resolveCompatScoreGate = createServerOnlyFn(
    async (input: {
        tokenUserProfileId: string;
        participantId?: string;
        conversationId?: string;
        listingId: string;
    }): Promise<{
        decision: CompatScoreGateDecision;
        userProfileId: string;
        forceDeterministic: boolean;
    }> => {
        const db = await getDb();

        const participant =
            input.participantId && input.conversationId
                ? await db.query.kitsConversationParticipants.findFirst({
                      columns: { userProfileId: true },
                      where: and(
                          eq(
                              kitsConversationParticipants.id,
                              input.participantId,
                          ),
                          eq(
                              kitsConversationParticipants.conversationId,
                              input.conversationId,
                          ),
                      ),
                  })
                : undefined;
        const userProfileId =
            participant?.userProfileId ?? input.tokenUserProfileId;

        const readLedger = () =>
            db.query.userProfileFacts.findFirst({
                columns: { value: true },
                where: and(
                    eq(userProfileFacts.userId, userProfileId),
                    eq(userProfileFacts.factKey, COMPAT_FREE_SCORE_FACT_KEY),
                ),
            });

        const [profile, priorFact] = await Promise.all([
            db.query.userProfiles.findFirst({
                columns: { isAnonymous: true },
                where: eq(userProfiles.id, userProfileId),
            }),
            readLedger(),
        ]);

        if (profile && !profile.isAnonymous) {
            return {
                decision: "signed_in",
                userProfileId,
                forceDeterministic: false,
            };
        }

        let priorFreeScore: { listingId: string | null } | null;
        if (priorFact) {
            priorFreeScore = parseFreeScoreLedger(priorFact.value);
        } else if (
            await claimCompatFreeScore({
                userProfileId,
                listingId: input.listingId,
            })
        ) {
            priorFreeScore = null;
        } else {
            const raced = await readLedger();
            priorFreeScore = raced
                ? parseFreeScoreLedger(raced.value)
                : { listingId: null };
        }

        const { decision, forceDeterministic } = decideCompatScoreGate({
            isAnonymous: true,
            priorFreeScore,
            requestedListingId: input.listingId,
        });
        return { decision, userProfileId, forceDeterministic };
    },
);

export const readFreeRevealListingId = createServerOnlyFn(
    async (userProfileId: string): Promise<string | null> => {
        const db = await getDb();
        const fact = await db.query.userProfileFacts.findFirst({
            columns: { value: true },
            where: and(
                eq(userProfileFacts.userId, userProfileId),
                eq(userProfileFacts.factKey, COMPAT_FREE_SCORE_FACT_KEY),
            ),
        });
        return fact
            ? (parseFreeScoreLedger(fact.value)?.listingId ?? null)
            : null;
    },
);

const claimCompatFreeScore = createServerOnlyFn(
    async (input: {
        userProfileId: string;
        listingId: string;
    }): Promise<boolean> => {
        const db = await getDb();
        const now = new Date().toISOString();
        const inserted = await db
            .insert(userProfileFacts)
            .values({
                userId: input.userProfileId,
                factKey: COMPAT_FREE_SCORE_FACT_KEY,
                value: { listingId: input.listingId, at: now },
                source: "tool",
                updatedAt: now,
            })
            .onConflictDoNothing()
            .returning({ factKey: userProfileFacts.factKey });
        return inserted.length > 0;
    },
);

export const releaseCompatFreeScore = createServerOnlyFn(
    async (input: { userProfileId: string }): Promise<void> => {
        const db = await getDb();
        await db
            .delete(userProfileFacts)
            .where(
                and(
                    eq(userProfileFacts.userId, input.userProfileId),
                    eq(userProfileFacts.factKey, COMPAT_FREE_SCORE_FACT_KEY),
                ),
            );
    },
);
